import React, { useContext, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useCart } from "./CartContext";
import { FiShare2 } from "react-icons/fi";
import {
  FaArrowLeft,
  FaLock,
  FaRegBuilding,
  FaRegHandshake,
  FaRegCheckCircle,
  FaShoppingCart,
} from "react-icons/fa";
import { ThemeContext } from "../context/themeContext";
import SampleProducts from "./SampleProducts";
import "./SingleProduct.css";
import { saveUserHistory } from "../saveUserHistory";
import { useAuth } from "../auth/useAuth";

function SingleProduct() {
  const { t3 } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { activeTheme } = useContext(ThemeContext);
  const { cart, addToCart, increaseQuantity, decreaseQuantity, formatPrice } = useCart();
  const { user } = useAuth();

  const createSlug = (name) =>
    name.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-");

  const product = useMemo(() => {
    if (location.state?.product) return location.state.product;
    return SampleProducts.find((p) => String(p.id) === String(t3));
  }, [t3, location.state]);

  useEffect(() => {
    if (product) saveUserHistory(product.name, user?.user_id);
  }, [product, user]);

  if (!product) {
    return (
      <div className={`sp-container ${activeTheme === "dark" ? "dark-mode" : ""}`}>
        <div style={{ textAlign: "center", padding: "100px 20px" }}>
          <h2>Product Not Found</h2>
          <button className="sp-back-btn" onClick={() => navigate("/dashboard")}>
            <FaArrowLeft /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const discountPercent = Math.round(
    ((product.originalPrice - product.price) / product.originalPrice) * 100
  );
  const inCart = cart.find((item) => item.id === product.id)?.quantity || 0;

  const handleShare = () => {
    const slug = createSlug(product.name);
    const shareUrl = `${window.location.origin}/share/${slug}-${product.id}.html`;
    if (navigator.share) {
      navigator.share({ title: product.name, text: product.shortDescription, url: shareUrl });
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert("Secure link copied!");
    }
  };

  const goBack = () => {
    navigate(`/product`);
  };

  const goShopping = () => {
    navigate(`/product`, { replace: true });
  };

  return (
    <div className={`sp-container ${activeTheme === "dark" ? "dark-mode" : ""}`}>

      <button className="sp-back-btn" onClick={goBack}>
        <FaArrowLeft /> Back to Products
      </button>

      {/* ═══════════════════════════════
          HERO — Neon Glassmorphism Card
          ═══════════════════════════════ */}
      <div className="sp-hero-wrapper">
        {/* Blurred product image as bg */}
        <div className="sp-hero-bg" style={{ backgroundImage: `url(/${product.image})` }} />
        {/* Dark overlay */}
        <div className="sp-hero-overlay" />

        {/* Animated corner glow accents */}
        <div className="sp-glow-tl" />
        <div className="sp-glow-br" />

        {/* Share FAB */}
        <button className="sp-share-fab" onClick={handleShare} aria-label="Share">
          <FiShare2 />
        </button>

        {/* Main glass body */}
        <div className="sp-glass-body">

          {/* Product image box */}
          <div className="sp-img-box">
            <img src={`/${product.image}`} alt={product.name} className="sp-product-img" />
          </div>

          {/* Info + actions */}
          <div className="sp-info-col">
            <h1 className="sp-name">{product.name}</h1>
            <p className="sp-short-desc">{product.shortDescription}</p>

            <div className="sp-price-row">
              <span className="sp-price">{formatPrice(product.price)}</span>
              <span className="sp-original-price">{formatPrice(product.originalPrice)}</span>
              <span className="sp-disc-badge">{discountPercent}% OFF</span>
            </div>

            <div className="sp-cta-row">
              {inCart > 0 ? (
                <div className="sp-qty">
                  <button className="sp-qty-btn" onClick={() => decreaseQuantity(product.id)}>−</button>
                  <span className="sp-qty-num">{inCart}</span>
                  <button className="sp-qty-btn" onClick={() => increaseQuantity(product.id)}>+</button>
                </div>
              ) : (
                <button className="sp-add-btn" onClick={() => addToCart(product)}>
                  ADD TO CART
                </button>
              )}
              <button className="sp-continue-btn" onClick={goShopping}>
                Continue Shopping
              </button>
            </div>
          </div>
        </div>

        {/* Cart float badge — bottom-left */}
        <div className="sp-cart-float">
          <FaShoppingCart className="sp-cart-icon" />
          {inCart > 0 && <span className="sp-cart-badge">+{inCart}</span>}
        </div>
      </div>

      {/* ─── Description ─── */}
      <div className="sp-section-card">
        <h3 className="sp-section-title">Description</h3>
        <p className="sp-desc-text">{product.description}</p>
      </div>

      {/* ─── Specs ─── */}
      <div className="sp-section-card sp-specs-grid">
        <div className="sp-spec">
          <span className="sp-spec-label">
            <FaRegBuilding className="sp-spec-icon" /> Sector
          </span>
          <span className="sp-spec-value">{product.sector || "General Business"}</span>
        </div>
        <div className="sp-spec">
          <span className="sp-spec-label">
            <FaRegHandshake className="sp-spec-icon" /> Support &amp; Training
          </span>
          <span className="sp-spec-value">{product.support || "Consultancy Available"}</span>
        </div>
        <div className="sp-spec">
          <span className="sp-spec-label">
            <FaRegCheckCircle className="sp-spec-icon" /> Status
          </span>
          <span className="sp-spec-value">{product.status || "Established"}</span>
        </div>
      </div>

      {/* ─── Statistics ─── */}
      <div className="sp-section-card">
        <div className="sp-stats-header">
          <h3 className="sp-section-title" style={{ margin: 0 }}>Statistics</h3>
          <FaLock className="sp-lock-icon" />
        </div>
        <p className="sp-locked-text">
          Upgrade your subscription to unlock detailed market analytics, growth trends,
          and revenue projections for this sector.
        </p>
      </div>

    </div>
  );
}

export default SingleProduct;
